import { Fragment, useState } from 'react';

import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';

import {
  ExpandMore,
  GroupOutlined,
  GroupWork,
  DriveFileRenameOutlineOutlined,
  SmartDisplayOutlined,
  ArticleOutlined,
} from '@mui/icons-material';
import Survey from '../../Main/Survey';
import { IToggleObj } from './type';
import { initialToggleObj } from './initialStates';
import useNotification from 'hooks/useNotification';
import usePatient from 'store/patient/usePatient';
import useSurvey from 'store/survey/useSurvey';
import { useNavigate } from 'react-router-dom';

interface Props {
  menuDrawerWidth: number;
  setMenuDrawerWidth: (menuDrawerWidth: number) => void;
  menuState: string;
  setMenuState: (menuState: string) => void;
}

const MenuRecords = (props: Props) => {
  const { menuDrawerWidth, setMenuDrawerWidth, setMenuState } = props;
  const [toggle, setToggle] = useState<IToggleObj>(initialToggleObj);
  const { patientInfo } = usePatient();
  const { onRequired } = useNotification();
  const navigate = useNavigate();
  const { onUpdateSurveyType } = useSurvey();

  const changeMenu = (path: string, menuState: string) => {
    navigate(path);
    setMenuState(menuState);
  };

  const changeAssignment = () =>
    changeMenu('/professorAuth/assignment', 'Даалгавар илгээлтийн шалгалт');

  const changeOriginMenu = () =>
    changeMenu(
      '/professorAuth/video',
      'Үндсэн сувилахуйн ур чадварын видео менежмент'
    );

  const changeMenuNurseRecord = () =>
    changeMenu(
      '/professorAuth/record',
      'Сувилахуйн бүрэн бүртгэлийн хянан үзэх'
    );

  const changeMenuList = () =>
    changeMenu('/professorAuth/list', 'Виртуал өвчтөний жагсаалт');

  const changeMenEditList = () =>
    changeMenu('/professorAuth/modify', 'Виртуал өвчтөний өөрчлөлт');

  const changeClassSection = () =>
    changeMenu('/professorAuth/classSection', 'Ангийн хэсэг үүсгэх ба удирдах');

  const menus = [
    {
      icon: <ArticleOutlined />,
      label: 'Даалгавар илгээлтийн шалгалт',
      onclick: changeAssignment,
    },
    {
      icon: <SmartDisplayOutlined />,
      label: 'Үндсэн сувилахуйн ур чадварын видео менежмент',
      onclick: changeOriginMenu,
    },
    {
      icon: <DriveFileRenameOutlineOutlined />,
      label: 'Сувилахуйн бүрэн бүртгэлийн хянан үзэх',
      onclick: changeMenuNurseRecord,
    },
    {
      disabled: true,
      icon: <GroupOutlined />,
      label: 'Виртуал өвчтөний удирдлага',
      id: 'nurse_record',
    },
    {
      label: 'Виртуал өвчтөний жагсаалт',
      toggle: toggle.nurse_record,
      onclick: changeMenuList,
    },
    {
      label: 'Виртуал өвчтөний өөрчлөлт',
      toggle: toggle.nurse_record,
      onclick: changeMenEditList,
    },
    {
      icon: <GroupWork />,
      label: 'Ангийн хэсэг үүсгэх ба удирдах',
      onclick: changeClassSection,
    },
  ];

  const onClickListItem = (label: string) => {
    if (!patientInfo) {
      return onRequired('REQUIRED.PATIENT');
    }
    onUpdateSurveyType(label);
    return;
  };

  const onClickDisabledItem = (sublabel: string | undefined) => {
    let newToggle = { ...toggle };

    if (menuDrawerWidth !== 220) {
      if (sublabel) newToggle[sublabel] = true;
      setToggle(newToggle);
      setMenuDrawerWidth(220);
      return;
    }

    if (sublabel) newToggle[sublabel] = !newToggle[sublabel];
    setToggle(newToggle);
  };

  return (
    <Fragment>
      {menus.map(({ icon, label, onclick, disabled, toggle, id }) => {
        const onClick = () => {
          if (disabled) onClickDisabledItem(id);
          else onClickListItem(label);
        };

        const MoreIcon = () => {
          if (!disabled) return null;
          return (
            <ExpandMore
              fontSize="small"
              sx={{ color: '#fff', marginRight: '-8px', marginLeft: '7px' }}
            />
          );
        };

        // 축소된 메뉴 (52px)
        if (menuDrawerWidth !== 220) {
          if (!icon) return null;

          return (
            <Tooltip
              key={label}
              title={label}
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: '14px',
                    padding: '8px 12px',
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  },
                },
                arrow: {
                  sx: {
                    color: 'rgba(0, 0, 0, 0.9)',
                  },
                },
              }}
            >
              <ListItem disablePadding>
                <ListItemButton onClick={onclick}>
                  <ListItemIcon>{icon}</ListItemIcon>
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        }

        // 확장된 메뉴 (220px)
        if (icon) {
          return (
            <ListItem key={label} disablePadding>
              <ListItemButton onClick={!disabled ? onclick : onClick}>
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText primary={label} />
                <MoreIcon />
              </ListItemButton>
            </ListItem>
          );
        }

        if (toggle) {
          return (
            <ListItem key={label} disablePadding>
              <ListItemButton className={''} onClick={onclick}>
                <ListItemIcon>{icon}</ListItemIcon>
                <ListItemText primary={label} sx={{ whiteSpace: 'pre-wrap' }} />
                <MoreIcon />
              </ListItemButton>
            </ListItem>
          );
        }

        return null;
      })}

      <Survey />
    </Fragment>
  );
};

export default MenuRecords;
